import { useEffect, useState, useCallback } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;

function urlBase64ToUint8Array(base64String) {
  const pad = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Small toggle to enable / disable browser push notifications for this device.
 * Registers /sw.js and subscribes via VAPID public key fetched from the backend.
 */
export default function PushToggle({ compact = false }) {
  const { user } = useAuth();
  const [state, setState] = useState("idle"); // idle|subscribing|subscribed|denied|unsupported
  const [busy, setBusy] = useState(false);

  const detect = useCallback(async () => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setState("unsupported"); return;
    }
    if (Notification.permission === "denied") { setState("denied"); return; }
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      setState(sub ? "subscribed" : "idle");
    } catch { setState("idle"); }
  }, []);

  useEffect(() => { detect(); }, [detect]);

  const enable = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setState("denied"); return; }
      const reg = await navigator.serviceWorker.register("/sw.js");
      const keyRes = await fetch(`${API}/api/push/public-key`);
      const { public_key } = await keyRes.json();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key),
      });
      await fetch(`${API}/api/push/subscribe`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sub.toJSON()),
      });
      setState("subscribed");
    } catch (e) { console.warn("[push] enable failed:", e); }
    setBusy(false);
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await fetch(`${API}/api/push/unsubscribe`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(sub.toJSON()),
        });
        await sub.unsubscribe();
      }
      setState("idle");
    } catch (e) { console.warn("[push] disable failed:", e); }
    setBusy(false);
  };

  if (state === "unsupported" || !user) return null;
  const enabled = state === "subscribed";
  const denied = state === "denied";
  const label = enabled ? "PUSH ATIVO" : denied ? "PUSH BLOQUEADO" : "ATIVAR PUSH";
  return (
    <button
      onClick={enabled ? disable : enable}
      disabled={busy || denied}
      data-testid="push-toggle"
      title={denied ? "Ative notificações do site nas configurações do navegador" : "Receber avisos de torneio começando"}
      className={`inline-flex items-center gap-2 rounded-sm border font-mono tracking-widest text-[10px] px-3 py-2 transition-colors ${enabled ? "border-emerald-400/60 bg-emerald-500/10 text-emerald-200" : denied ? "border-red-400/40 bg-red-500/10 text-red-200 opacity-60" : "border-primary/50 bg-black/50 text-primary hover:bg-primary/10"} ${compact ? "" : ""}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : enabled ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
      {label}
    </button>
  );
}
